import { useState, useCallback } from 'react';
import { Position, ZoomState } from '../types';
import { MIN_SCALE, MAX_SCALE, ZOOM_FACTOR_IN, ZOOM_FACTOR_OUT } from '../constants';
import { calculateZoomPosition, mapScreenPosition } from '../utils/positionUtils';

type WheelLikeEvent = Pick<WheelEvent, 'clientX' | 'clientY' | 'deltaY' | 'preventDefault'>;

interface UseZoomProps {
  initialScale?: number;
  initialPosition?: Position;
  invertView?: boolean;
}

export const useZoom = ({ 
  initialScale = 1, 
  initialPosition = { x: 0, y: 0 },
  invertView = false
}: UseZoomProps = {}) => {
  const [zoomState, setZoomState] = useState<ZoomState>({
    scale: initialScale,
    position: initialPosition
  });

  // Track table dragging state
  const [isTableDragging, setIsTableDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Handle starting table drag (when shift is pressed)
  const handleTableDragStart = useCallback((e: React.MouseEvent, containerRect: DOMRect) => {
    if (e.button !== 0) {
      return;
    }

    e.preventDefault();
    setIsTableDragging(true);

    const mapped = mapScreenPosition(e.clientX, e.clientY, containerRect, invertView);

    setDragStart({
      // We keep coordinates in the container space
      x: mapped.x - zoomState.position.x,
      y: mapped.y - zoomState.position.y
    });
  }, [zoomState.position.x, zoomState.position.y, invertView]);

  // Update position while dragging the table
  const handleTableDrag = useCallback((e: React.MouseEvent, containerRect: DOMRect) => {
    if (!isTableDragging) return;
    
    // Calculate new position based on mouse movement
    const mapped = mapScreenPosition(e.clientX, e.clientY, containerRect, invertView);
    const newPosition = {
      x: mapped.x - dragStart.x,
      y: mapped.y - dragStart.y,
    };
    // Update the zoom state with the new position
    setZoomState(prev => ({
      ...prev,
      position: newPosition
    }));
  }, [isTableDragging, dragStart, invertView]);

  // End table dragging
  const handleTableDragEnd = useCallback(() => {
    if (isTableDragging) {
      setIsTableDragging(false);
    }
  }, [isTableDragging]);

  // Handle zooming with mouse wheel
  const handleZoom = useCallback((e: WheelLikeEvent, containerRect: DOMRect) => {
    e.preventDefault();
    
    // Get mouse position relative to the container
    const mapped = mapScreenPosition(e.clientX, e.clientY, containerRect, invertView);
    const mouseX = mapped.x;
    const mouseY = mapped.y;
    
    // Calculate zoom factor based on scroll direction
    const zoomFactor = e.deltaY < 0 ? ZOOM_FACTOR_IN : ZOOM_FACTOR_OUT;
    
    // Calculate new scale
    const newScale = zoomState.scale * zoomFactor;
    
    // Limit zoom range
    if (newScale < MIN_SCALE || newScale > MAX_SCALE) return;
    
    // Calculate new position to zoom toward mouse cursor
    const newPosition = calculateZoomPosition(
      mouseX,
      mouseY,
      zoomState.position,
      zoomFactor
    );
    setZoomState({
      scale: newScale,
      position: newPosition,
    });
  }, [zoomState, invertView]);

  // Reset zoom to initial state
  const resetZoom = useCallback(() => {
    setZoomState({
      scale: initialScale,
      position: initialPosition
    });
  }, [initialScale, initialPosition]);

  return {
    zoomState,
    setZoomState,
    isTableDragging,
    handleTableDragStart,
    handleTableDrag,
    handleTableDragEnd,
    handleZoom,
    resetZoom
  };
}; 
